import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersService } from '../../services/api';
import { User, CreateAdminData } from '../../types';

interface UsersState {
  consumers: User[];
  admins: User[];
  isLoading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  consumers: [],
  admins: [],
  isLoading: false,
  error: null,
};

export const fetchConsumers = createAsyncThunk(
  'users/fetchConsumers',
  async (_, { rejectWithValue }) => {
    try {
      return await usersService.getConsumers();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch consumers');
    }
  }
);

export const fetchAdmins = createAsyncThunk(
  'users/fetchAdmins',
  async (_, { rejectWithValue }) => {
    try {
      return await usersService.getAdmins();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admins');
    }
  }
);

export const createAdmin = createAsyncThunk(
  'users/createAdmin',
  async (data: CreateAdminData, { rejectWithValue }) => {
    try {
      return await usersService.createAdmin(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create admin');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await usersService.deleteUser(userId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

export const suspendUser = createAsyncThunk(
  'users/suspendUser',
  async ({ userId, suspend }: { userId: string; suspend: boolean }, { rejectWithValue }) => {
    try {
      const user = await usersService.suspendUser(userId, suspend);
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user status');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Consumers
      .addCase(fetchConsumers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConsumers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.isLoading = false;
        state.consumers = action.payload;
      })
      .addCase(fetchConsumers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Admins
      .addCase(fetchAdmins.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.isLoading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Admin
      .addCase(createAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.admins.push(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.consumers = state.consumers.filter((u) => u._id !== action.payload);
        state.admins = state.admins.filter((u) => u._id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Suspend User
      .addCase(suspendUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(suspendUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        const updatedUser = action.payload;
        const consumerIndex = state.consumers.findIndex((u) => u._id === updatedUser._id);
        if (consumerIndex !== -1) {
          state.consumers[consumerIndex] = updatedUser;
        }
        const adminIndex = state.admins.findIndex((u) => u._id === updatedUser._id);
        if (adminIndex !== -1) {
          state.admins[adminIndex] = updatedUser;
        }
      })
      .addCase(suspendUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = usersSlice.actions;
export default usersSlice.reducer;
